import { inject, Injectable, Provider } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import {
  CustomerGetQuery, CustomerGetQueryResult,
  IUserInformationGetQueryResult,
  UserClient, UserCustomerListGetQuery, UserCustomerListGetQueryResult, UserDeleteCommand, UserDeleteCommandResult, UserFavoritesInitializeCommand,
  UserFavoritesInitializeCommandResult, UserGetQuery, UserGetQueryResult, UserIDPLoginVerifyCommand, UserInformationGetQuery, UserInformationSetCommand,
  UserInformationSetCommandResult, UserListGetQuery, UserListGetQueryResult, UserLoginCommand,
  UserPasswordResetCommand, UserPasswordResetCommandResult, UserPasswordResetLinkSendCommand, UserPasswordResetLinkSendCommandResult,
  UserPasswordResetLinkVerifyCommand, UserPasswordResetLinkVerifyCommandResult, UserPasswordSetCommand, UserPasswordSetCommandResult,
  UserRegisterCommand, UserRegisterCommandResult, UserSetCommand, UserSetCommandResult, UserSwitchCustomerCommand, UserTempLoginVerifyCommand
} from '../../INTEGRA_Shop_Client';
import { IUserApiService, IUserApiServiceToken } from './user-api-service.interface';

@Injectable({
  providedIn: 'root'
})
export class UserApiService implements IUserApiService {
  private readonly userClient: UserClient = inject(UserClient);
  private userInformationGetSubscription: Subscription | undefined;

  public userLogin(customerNumber: string, coUserNumberOrEmailOrAlias: string, password: string): Observable<string> {
    let command = new UserLoginCommand({
      customerNumber: customerNumber,
      coUserNumberOrEMailOrAlias: coUserNumberOrEmailOrAlias,
      password: password
    });
    return this.userClient.userLogin(command);
  }

  public userIDPLogin(userData: any, accessToken: string, idToken: string): Observable<string> {
    let command = new UserIDPLoginVerifyCommand({
      userData: userData,
      accessToken: accessToken,
      idToken: idToken
    });
    return this.userClient.userIDPLogin(command);
  }

  public userLogout(): Observable<void> {
    return this.userClient.userLogout();
  }

  public loginAsMitben(key: string): Observable<string> {
    let command = new UserTempLoginVerifyCommand({
      tempLoginLinkUniqueKey: key
    });
    return this.userClient.userTempLogin(command);
  }

  public userLoginByRefreshToken(): Observable<string> {
    return this.userClient.userLoginByRefreshToken();
  }

  public userInformationGet(
    params?: Partial<UserInformationGetQuery>,
    allowCancellation: boolean = false
  ): Observable<IUserInformationGetQueryResult> {
    let query = new UserInformationGetQuery({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      customerNumber: params?.customerNumber,
    });

    // no cancellation: normal request
    if (!allowCancellation) {
      return this.userClient.userInformationGet(query);
    }

    // cancellation: kill old subscription
    if (this.userInformationGetSubscription) {
      this.userInformationGetSubscription.unsubscribe();
      this.userInformationGetSubscription = undefined;
    }

    // new observable with manuel subscription management
    return new Observable<IUserInformationGetQueryResult>(observer => {
      this.userInformationGetSubscription = this.userClient.userInformationGet(query).subscribe({
        next: (result) => observer.next(result),
        error: (error) => {
          observer.error(error);
          this.userInformationGetSubscription = undefined;
        },
        complete: () => {
          observer.complete();
          this.userInformationGetSubscription = undefined;
        }
      });

      // cleanup upon component unsubscribed
      return () => {
        if (this.userInformationGetSubscription) {
          this.userInformationGetSubscription.unsubscribe();
          this.userInformationGetSubscription = undefined;
        }
      };
    });
  }

  public userInformationSet(command: UserInformationSetCommand): Observable<UserInformationSetCommandResult> {
    return this.userClient.userInformationSet(command);
  }

  public userGet(userNumber?: string | undefined, loadOnlyActive?: boolean | undefined): Observable<UserGetQueryResult> {
    let query = new UserGetQuery({
      userNumber: userNumber,
      loadOnlyActive: loadOnlyActive
    });
    return this.userClient.userGet(query);
  }

  public userSet(command: UserSetCommand): Observable<UserSetCommandResult> {
    return this.userClient.userSet(command);
  }

  public userListGet(): Observable<UserListGetQueryResult> {
    let query = new UserListGetQuery();
    return this.userClient.userListGet(query);
  }

  public userDelete(userNumber?: string | undefined): Observable<UserDeleteCommandResult> {
    let command = new UserDeleteCommand({
      userNumber: userNumber
    });
    return this.userClient.userDelete(command);
  }

  public userPasswordReset(command: UserPasswordResetCommand): Observable<UserPasswordResetCommandResult> {
    return this.userClient.userPasswordReset(command);
  }

  public userPasswordResetLinkSend(command: UserPasswordResetLinkSendCommand): Observable<UserPasswordResetLinkSendCommandResult> {
    return this.userClient.userPasswordResetLinkSend(command);
  }

  public userPasswordResetLinkVerify(command: UserPasswordResetLinkVerifyCommand): Observable<UserPasswordResetLinkVerifyCommandResult> {
    return this.userClient.userPasswordResetLinkVerify(command);
  }

  public userPasswordSet(command: UserPasswordSetCommand): Observable<UserPasswordSetCommandResult> {
    return this.userClient.userPasswordSet(command);
  }

  public customerGet(customerNumber: string | undefined): Observable<CustomerGetQueryResult> {
    let query = new CustomerGetQuery({
      number: customerNumber
    });
    return this.userClient.customerGet(query);
  }

  public userCustomerListGet(): Observable<UserCustomerListGetQueryResult> {
    let query = new UserCustomerListGetQuery();
    return this.userClient.userCustomerListGet(query);
  }

  public userSwitchCustomer(customerNumber?: string | undefined): Observable<string> {
    let command = new UserSwitchCustomerCommand({
      customerNumber: customerNumber,
    });
    return this.userClient.userSwitchCustomer(command);
  }

  public userRegister(command: UserRegisterCommand): Observable<UserRegisterCommandResult> {
    return this.userClient.userRegister(command);
  }

  public userFavoritesInitialize(): Observable<UserFavoritesInitializeCommandResult> {
    let command = new UserFavoritesInitializeCommand();
    return this.userClient.userFavoritesInitialize(command);
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const UserApiServiceProvider: Provider[] = [
  { provide: UserClient },
  { provide: IUserApiServiceToken, useExisting: UserApiService }
];
